import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import SVG from "react-inlinesvg";
import { FolderCardContainer } from "./styles.js";
import moment from "moment";
import DotsActionMenu from "../../DotsActionMenu/index.js";
import classNames from "classnames";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { getActionsMoveToFolder } from "../utils.js";
import { motion } from "framer-motion";
import { projectUrlId } from "../../../models/Project.js";
import { AppEventPublisher } from "../../../AppEventPublisher.js";
import ProjectCover from "../ProjectCover";
import EditableTitle from "../EditableTitle";

const SHELVES = 2;
const BOOKS_PER_SHELF = 4;
const SHELF_CAPACITY = SHELVES * BOOKS_PER_SHELF;
const SHELF_BOOK_WIDTH = 45.45;
const SINGLE_SHELF_MAX_BOOKS = 2;
const SINGLE_SHELF_BOOK_WIDTH = 78;
const SINGLE_SHELF_WIDTH_PER_EXTRA_BOOK = 7;
const MAX_OVERFLOW_LABEL = 99;

// A single book is centered; otherwise books span the shelf, overlapping more as it fills.
function getShelfBookLefts(count, bookWidth) {
  if (count === 1) return [(100 - bookWidth) / 2];
  const step = (100 - bookWidth) / (count - 1);
  return Array.from({ length: count }, (_, index) => index * step);
}

// Bottom shelf takes the extra book on odd counts.
function splitAcrossShelves(books) {
  const topCount = Math.floor(books.length / 2);
  return [books.slice(0, topCount), books.slice(topCount)];
}

function ShelfBook({ project, index, left, width, active, onOpen, onOpenEnter, onOpenFocus, onOpenBlur }) {
  return (
    <div
      className={classNames("shelf-book", { active })}
      style={{ left: `${left}%`, width: `${width}%`, zIndex: index + 1 }}
    >
      <div className="shelf-book-inner">
        <ProjectCover
          imageUrl={project.cover_image_url}
          title={project.title}
          showTitle
        >
          {project.overflowCount > 0 && (
            <div
              className="shelf-overflow"
              role="img"
              aria-label={`and ${project.overflowCount} more ${project.overflowCount === 1 ? "project" : "projects"}`}
            >
              <span aria-hidden="true">+{project.overflowCount > MAX_OVERFLOW_LABEL ? `${MAX_OVERFLOW_LABEL}+` : project.overflowCount}</span>
            </div>
          )}
        </ProjectCover>
        {onOpen && (
          <button
            type="button"
            className="shelf-open use-component-styles"
            aria-label={`Open project: ${project.title}`}
            onMouseEnter={onOpenEnter}
            onFocus={onOpenFocus}
            onBlur={onOpenBlur}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onOpen}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2.5 6h7M6.5 3l3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

const SWITCH_DELAY_MS = 60;
const SWITCH_DELAY_UNDER_ACTIVE_MS = 350;

function ShelfRow({ books, bookWidth, activeIndex, onActivate }) {
  const history = useHistory();
  const switchTimer = useRef(null);
  const lefts = getShelfBookLefts(books.length, bookWidth);

  useEffect(() => () => clearTimeout(switchTimer.current), []);

  // Strips under the active book are on the pointer's path to its open button, so they need a real dwell before taking over.
  const requestActivate = (index) => {
    clearTimeout(switchTimer.current);
    if (activeIndex === null || activeIndex === index) {
      onActivate(index);
      return;
    }
    const isUnderActiveBook = index > activeIndex && lefts[index] < lefts[activeIndex] + bookWidth;
    switchTimer.current = setTimeout(() => onActivate(index), isUnderActiveBook ? SWITCH_DELAY_UNDER_ACTIVE_MS : SWITCH_DELAY_MS);
  };

  const openProject = (event, project) => {
    event.stopPropagation();
    AppEventPublisher.projects.openProject(project.source || "unknown");
    const url = projectUrlId(project.firebase_id);
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      history.push(url);
    }
  };

  return (
    <div
      className="shelf-books"
      onMouseLeave={() => {
        clearTimeout(switchTimer.current);
        onActivate(null);
      }}
    >
      {books.map((project, index) => (
        <ShelfBook
          key={project.id}
          project={project}
          index={index}
          left={lefts[index]}
          width={bookWidth}
          active={activeIndex === index}
          onOpen={project.is_importing || project.overflowCount ? undefined : (event) => openProject(event, project)}
          onOpenEnter={() => clearTimeout(switchTimer.current)}
          onOpenFocus={() => {
            clearTimeout(switchTimer.current);
            onActivate(index);
          }}
          onOpenBlur={(event) => {
            if (!event.currentTarget.closest(".shelf-books").contains(event.relatedTarget)) onActivate(null);
          }}
        />
      ))}
      {books.map((project, index) => (
        <div
          key={project.id}
          className="shelf-hit"
          style={{ left: `${lefts[index]}%`, width: `${index === books.length - 1 ? bookWidth : Math.min(lefts[index + 1] - lefts[index], bookWidth)}%` }}
          onMouseEnter={() => requestActivate(index)}
        />
      ))}
    </div>
  );
}

function ShelfFolderCard({ isSeries, folder, entries, projectCount, onRestore, onArchiveDelete, onOpen, onMoveToFolder, onDelete, onRename, foldersById, foldersByParentId }) {
  const { setNodeRef: setNodeRefDroppable, active, isOver } = useDroppable({ id: folder.id });
  const { attributes, listeners, setNodeRef: setNodeRefDraggable, transform, isDragging } = useDraggable({ id: folder.id, data: { type: "folder" } });
  const [activeBook, setActiveBook] = useState(null);

  const actions = (() => {
    if (!folder.deleted && !folder.archived) {
      return {
        moveToFolder: {
          label: "Move to...",
          icon: (
            <SVG
              width={24}
              height={24}
              src="/assets/move-to.svg"
              className="folder"
              aria-hidden="true"
            />
          ),
          onClick: () => {
            return getActionsMoveToFolder(foldersById, foldersByParentId, onMoveToFolder, folder.id, false);
          },
        },
        deleteFolder: {
          label: "Delete",
          danger: true,
          icon: (
            <SVG
              width={24}
              height={24}
              src="/assets/delete.svg"
              className="trash"
              aria-hidden="true"
            />
          ),
          onClick: () => {
            onDelete(folder.id);
          },
        },
      };
    } else if (folder.archived) {
      // TODO: Unarchive folder
      return {};
    } else if (folder.deleted) {
      return {
        restoreFolder: {
          label: "Restore",
          iconStroke: true,
          icon: (
            <SVG
              width={24}
              height={24}
              src="/assets/restore.svg"
              className="restore"
              aria-hidden="true"
            />
          ),
          onClick: () => {
            onRestore();
          },
        },
        permanentDeletedFolder: {
          label: "Permanently Delete",
          danger: true,
          icon: (
            <SVG
              width={24}
              height={24}
              src="/assets/delete.svg"
              className="trash"
              aria-hidden="true"
            />
          ),
          onClick: () => {
            onArchiveDelete();
          },
        },
      };
    }
  })();

  const [dragPlaceholderText, setDragPlaceholderText] = useState("Drag project here");

  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;

  const activeType = active?.data?.current?.type;

  useEffect(() => {
    if (activeType === "project") {
      setDragPlaceholderText("Drag project here");
    } else if (activeType === "folder") {
      setDragPlaceholderText("Drag folder here");
    }
  }, [activeType]);

  const allBooks = entries.filter((entry) => entry.type === "project").map((entry) => entry.project);
  const overflowCount = Math.max(0, allBooks.length - SHELF_CAPACITY);
  const books = allBooks.slice(0, SHELF_CAPACITY).map((project, index, shown) => (overflowCount > 0 && index === shown.length - 1 ? { ...project, overflowCount } : project));
  const isSingleShelf = books.length > 0 && books.length <= SINGLE_SHELF_MAX_BOOKS;
  const shelfBooks = isSingleShelf ? [books] : splitAcrossShelves(books);
  const countLabel = `${projectCount} ${projectCount === 1 ? "project" : "projects"}`;

  return (
    <FolderCardContainer
      as={motion.div}
      layout={!isDragging ? "position" : false}
      ref={setNodeRefDroppable}
      className={classNames({ active: active && !isDragging, isOver })}
    >
      <div
        className={classNames("folder", { isDragging })}
        onClick={onOpen}
        {...attributes}
        {...listeners}
        ref={setNodeRefDraggable}
        style={style}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !isDragging) {
            event.preventDefault();
            onOpen();
          }
        }}
        aria-label={`Open folder: ${folder.title}`}
      >
        <div className="folder-tile">
          <div className="shelves">
            {entries.length > 0 ? (
              Array.from({ length: shelfBooks.length }, (_, shelfIndex) => (
                <div
                  key={shelfIndex}
                  className="shelf"
                  style={isSingleShelf ? { top: "4%", height: "94%" } : { top: `${4 + shelfIndex * 48}%` }}
                >
                  <ShelfRow
                    books={shelfBooks[shelfIndex]}
                    bookWidth={isSingleShelf ? SINGLE_SHELF_BOOK_WIDTH - (books.length - 1) * SINGLE_SHELF_WIDTH_PER_EXTRA_BOOK : SHELF_BOOK_WIDTH}
                    activeIndex={activeBook?.shelf === shelfIndex ? activeBook.index : null}
                    onActivate={(index) => setActiveBook(index === null ? null : { shelf: shelfIndex, index })}
                  />
                </div>
              ))
            ) : (
              <span className="shelves-empty">Empty folder</span>
            )}
          </div>
          <div className="drag-to-folder">{dragPlaceholderText}</div>
          {isSeries && (
            <div className="series-tag">
              <SVG
                src="/assets/series.svg"
                height={16}
                width={16}
                aria-hidden="true"
              />
              <span>series</span>
            </div>
          )}
        </div>
        <div className="folder-title">
          <EditableTitle
            title={folder.title}
            onRename={onRename}
          />
          <DotsActionMenu
            actions={actions}
            iconSize={16}
            position="bottom-start"
            size={24}
            ariaLabel={`More options for ${folder.title}`}
          />
        </div>
        <div className="folder-subtitle">
          <p className="folder-word-count">{countLabel}</p>
          <p className="folder-last-edited">{moment(folder.last_active).fromNow()}</p>
        </div>
      </div>
    </FolderCardContainer>
  );
}

export default function FolderCard(props) {
  const isSeries = !!props.folder.series_id || !!props.folder.is_pending_series;
  return (
    <ShelfFolderCard
      {...props}
      isSeries={isSeries}
    />
  );
}
